import React from "react";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaHardHat,
  FaAward,
  FaHandshake,
  FaStar,
  FaLightbulb,
  FaShieldAlt
} from "react-icons/fa";

import "./About.css";
import { aboutData, coreValues, stats, teamMembers } from "../data/siteData";

const About = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const renderCoreIcon = (type) => {
    switch (type) {
      case "quality":
        return <FaStar />;
      case "integrity":
        return <FaHandshake />;
      case "innovation":
        return <FaLightbulb />;
      case "safety":
        return <FaShieldAlt />;
      default:
        return null;
    }
  };

  return (
    <div className="about-page">

      {/* PAGE HEADER */}
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>About Mahabali</h1>
            <p>Building Excellence Since 2010</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT CONTENT */}
      <section className="about-content-section">
        <div className="container">
          <div className="about-grid">

            <motion.div {...fadeInUp} className="about-image-wrapper">
              <img src={aboutData.image} alt="About Mahabali" />
              <div className="image-stats">
                <div className="image-stat">
                  <span className="image-stat-number">
                    {aboutData.yearsOfExperience}
                  </span>
                  <span className="image-stat-label">Years Experience</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              {...fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="about-text-wrapper"
            >
              <h2>{aboutData.title}</h2>
              <p className="lead-text">{aboutData.description}</p>
              <p>{aboutData.missionStatement}</p>

              <div className="about-highlights">
                <div className="highlight-item">
                  <FaAward className="highlight-icon" />
                  <div>
                    <h4>Quality Assured</h4>
                    <p>Certified and insured for your peace of mind</p>
                  </div>
                </div>

                <div className="highlight-item">
                  <FaUsers className="highlight-icon" />
                  <div>
                    <h4>Expert Team</h4>
                    <p>Skilled professionals with decades of experience</p>
                  </div>
                </div>

                <div className="highlight-item">
                  <FaHandshake className="highlight-icon" />
                  <div>
                    <h4>Customer Focus</h4>
                    <p>Your satisfaction is our top priority</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section className="stats-showcase bg-light">
        <div className="container">
          <div className="stats-showcase-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.id}
                className="stat-showcase-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <h3 className="stat-showcase-number">{stat.number}</h3>
                <p className="stat-showcase-label">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="core-values-section">
        <div className="container">

          <motion.div {...fadeInUp} className="section-header">
            <h2>Our Core Values</h2>
            <p>The principles that drive our success</p>
          </motion.div>

          <div className="values-detailed-grid">
            {coreValues.map((value, index) => (
              <motion.div
                key={value.id}
                className="value-detailed-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="value-detailed-icon">
                  {renderCoreIcon(value.icon)}
                </div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* TEAM */}
      <section className="team-section bg-light">
        <div className="container">

          <motion.div {...fadeInUp} className="section-header">
            <h2>Meet Our Team</h2>
            <p>The experts behind our success</p>
          </motion.div>

          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.id}
                className="team-card"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="team-avatar">
                  <FaHardHat />
                </div>
                <div className="team-info">
                  <h3>{member.name}</h3>
                  <p className="team-position">{member.position}</p>
                  <p className="team-specialty">{member.specialty}</p>
                  <p className="team-bio">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>

    </div>
  );
};

export default About;